import React, { useEffect, useState } from 'react';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { MaskedInput } from "@/components/ui/masked-input";
import { Textarea } from "@/components/ui/textarea";
import { AddressInput } from '@/components/passport/AddressInput';
import { Switch } from "@/components/ui/switch";
import { UseFormReturn } from 'react-hook-form';
import { ClientFormData } from './ClientFormValidation';
import { LoyaltyStatusSelector } from '@/components/ui/loyalty-status-selector';
import { PassportCodeInput } from '@/components/passport/PassportCodeInput';

interface IndividualClientFormProps {
  form: UseFormReturn<ClientFormData>;
  autoFocus?: boolean;
}

export const IndividualClientForm: React.FC<IndividualClientFormProps> = ({ form, autoFocus = false }) => {
  const [subdivisionCode, setSubdivisionCode] = useState(form.getValues('passportSubdivisionCode') || '');
  const [issuedBy, setIssuedBy] = useState(form.getValues('passportIssuedBy') || '');
  const [birthDisplay, setBirthDisplay] = useState<string>('');

  useEffect(() => {
    const v = form.getValues('birthDate');
    if (!v) {
      setBirthDisplay('');
      return;
    }
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v));
    if (m) {
      const [, y, mo, d] = m;
      setBirthDisplay(`${d} ${mo} ${y}`);
    }
  }, [form]);

  const toDisplay = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    const dd = digits.slice(0, 2);
    const mm = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    return [dd, mm, yyyy].filter(Boolean).join(' ');
  };

  const toISO = (display: string): string | null => {
    const digits = display.replace(/\D/g, '');
    if (digits.length !== 8) return null;
    const dd = digits.slice(0, 2);
    const mm = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleSubdivisionCodeChange = (code: string) => {
    setSubdivisionCode(code);
    form.setValue('passportSubdivisionCode', code);
  };

  const handleIssuedByChange = (issuedByValue: string) => {
    setIssuedBy(issuedByValue);
    form.setValue('passportIssuedBy', issuedByValue);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <FormField
        control={form.control}
        name="fullName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>ФИО *</FormLabel>
            <FormControl>
              <Input 
                placeholder="Введите ФИО клиента" 
                {...field} 
                autoFocus={autoFocus}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="phone"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Телефон</FormLabel>
            <FormControl>
              <MaskedInput 
                mask="+9 (999) 999-99-99" 
                placeholder="+7 (999) 123-45-67" 
                value={field.value || ''}
                onChange={field.onChange}
                name={field.name}
                onBlur={field.onBlur}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input placeholder="example@email.com" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="birthDate"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Дата рождения</FormLabel>
            <FormControl>
              <Input
                type="text"
                placeholder="ДД ММ ГГГГ"
                inputMode="numeric"
                value={birthDisplay}
                onChange={(e) => {
                  const next = toDisplay(e.target.value);
                  setBirthDisplay(next);
                  const iso = toISO(next);
                  if (iso) field.onChange(iso);
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
 
      

      {/* Birthday Reminder (right column) */}
      <div className="md:col-start-2">
        <div className="space-y-3">
          <FormField
            control={form.control}
            name="remindBirthday"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="text-sm font-normal">
                  Включить напоминания о ДР
                </FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {form.watch('remindBirthday') && (
            <FormField
              control={form.control}
              name="remindDaysBefore"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>За сколько дней напомнить</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="1" 
                      max="30"
                      placeholder="3" 
                      {...field}
                      onChange={e => field.onChange(parseInt(e.target.value) || 3)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      </div>

      {/* Passport Information Section */}
      <div className="md:col-span-2 mt-4">
        <h3 className="text-lg font-medium mb-4">Паспортные данные</h3>
        <div className="space-y-4">
          {/* Первая строка: Код подразделения, Серия, Номер */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="passportSubdivisionCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Код подразделения</FormLabel>
                  <FormControl>
                    <PassportCodeInput
                      code={field.value || ''}
                      onCodeChange={field.onChange}
                      issuedBy={form.getValues('passportIssuedBy') || ''}
                      onIssuedByChange={(issuedByValue) => form.setValue('passportIssuedBy', issuedByValue)}
                      showLabel={false}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="passportSeries"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Серия паспорта</FormLabel>
                  <FormControl>
                    <Input placeholder="1234" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="passportNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Номер паспорта</FormLabel>
                  <FormControl>
                    <Input placeholder="567890" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Вторая строка: Кем выдан и Дата выдачи */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="passportIssuedBy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Кем выдан</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Кем выдан паспорт" 
                      {...field} 
                      value={issuedBy || field.value || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setIssuedBy(value);
                        field.onChange(value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="passportIssueDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Дата выдачи</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem className="md:col-span-3">
                <FormControl>
                  <AddressInput 
                    address={field.value || ''} 
                    onAddressChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      

      {/* Loyalty and Comment Section */}
      <div className="md:col-span-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="loyaltyStatus"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Статус лояльности</FormLabel>
                <FormControl>
                  <LoyaltyStatusSelector
                    value={field.value || 'gray'}
                    onValueChange={field.onChange}
                    size="md"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="comment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Комментарий</FormLabel>
                <FormControl>
                  <Textarea placeholder="Дополнительные заметки о клиенте" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
};
